import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule, UrlTree } from '@angular/router';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { MzicButtonComponent } from '@mzic/mzic-arpeggio-ui';
import { SwitchLanguageComponent } from '@mzic/mzic-components';
import { environment } from '@mzic/mzic-environments';
import { LanguageService } from '@mzic/mzic-services';
import { IconDiscordComponent } from '../../icons/icon-discord.component';
import { IconXTwitterComponent } from '../../icons/icon-x-twitter.component';
import { IconInstagramComponent } from './../../icons/icon-instagram.component';

@Component({
  selector: 'mzic-header',
  imports: [
    CommonModule,
    IconDiscordComponent,
    IconXTwitterComponent,
    IconInstagramComponent,
    SwitchLanguageComponent,
    TranslocoModule,
    RouterModule,
    MzicButtonComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  providers: [
    provideTranslocoScope({
      scope: 'home',
      alias: 'lang',
    }),
  ],
})
export class HeaderComponent implements AfterViewInit, OnDestroy {
  @Input() homeFocused = true;
  @Output() downloadAppClicked = new EventEmitter<void>();
  @ViewChild('menuNavbar', { read: ElementRef }) menuNavbar?: ElementRef<HTMLElement>;

  protected openMenu = signal(false);
  protected activeLanguage!: string;
  protected currentLanguage = 'en';
  protected instagramUrl = environment.instagramUS;
  protected artistsUrl = environment.artistsUrl;
  protected downloadAppUrl = 'https://mzic.io/download'; // TODO: Update with actual URL

  languageService = inject(LanguageService);
  translocoService = inject(TranslocoService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  // Swipe properties
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private isSwiping = false;
  private swipeInitialized = false;
  private swipeInitRetryCount = 0;
  private swipeInitTimeouts: number[] = [];
  private readonly MAX_SWIPE_INIT_RETRIES = 3;
  
  // Mathematical constants for swipe detection
  private readonly SWIPE_MIN_DISTANCE = 50; // Minimum pixels to consider as swipe
  private readonly SWIPE_MAX_TIME = 300; // Maximum milliseconds for swipe gesture
  private readonly SWIPE_MIN_VELOCITY = 0.15; // Minimum velocity (pixels/ms)
  private readonly SWIPE_ANGLE_THRESHOLD = 45; // Maximum angle deviation from horizontal (degrees)
  private readonly supportedLanguages = new Set(['pt-br', 'en']);

  protected handleMenuClick() {
    this.openMenu.update((value) => !value);
  }

  constructor() {
    // check the current language using transloco - with automatic unsubscribe
    this.translocoService.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lang) => {
        this.currentLanguage = lang;
        if (lang === 'pt-br') {
          this.instagramUrl = environment.instagramBR;
        } else {
          this.instagramUrl = environment.instagramUS;
        }
      });

    // Use effect to watch menu state changes
    effect(() => {
      const isOpen = this.openMenu();
      if (isOpen) {
        // Menu opened, initialize swipe after animation completes
        const timeoutId = window.setTimeout(() => this.initializeSwipeGestures(), 250);
        this.swipeInitTimeouts.push(timeoutId);
      } else {
        // Menu closed, cleanup swipe
        this.cleanupSwipeListeners();
      }
    });
  }

  ngAfterViewInit() {
    // Initial check if menu is already open
    if (this.openMenu()) {
      this.initializeSwipeGestures();
    }
  }

  ngOnDestroy() {
    // Clean up all timeouts
    this.swipeInitTimeouts.forEach(id => window.clearTimeout(id));
    this.swipeInitTimeouts = [];
    
    // Clean up swipe listeners if they exist
    this.cleanupSwipeListeners();
  }

  private initializeSwipeGestures() {
    // Only initialize on mobile
    if (window.innerWidth > 768) return;
    
    // Prevent duplicate initialization
    if (this.swipeInitialized) return;

    const menuElement = this.menuNavbar?.nativeElement;
    if (!menuElement) {
      // Retry if element not ready
      if (this.swipeInitRetryCount < this.MAX_SWIPE_INIT_RETRIES) {
        this.swipeInitRetryCount++;
        const timeoutId = window.setTimeout(() => this.initializeSwipeGestures(), 100);
        this.swipeInitTimeouts.push(timeoutId);
      }
      return;
    }

    // Verify element is visible
    const rect = menuElement.getBoundingClientRect();
    if (rect.width === 0) {
      // Element not visible yet, retry
      if (this.swipeInitRetryCount < this.MAX_SWIPE_INIT_RETRIES) {
        this.swipeInitRetryCount++;
        const timeoutId = window.setTimeout(() => this.initializeSwipeGestures(), 100);
        this.swipeInitTimeouts.push(timeoutId);
      }
      return;
    }

    // Add swipe listeners
    menuElement.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    menuElement.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    menuElement.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    
    this.swipeInitialized = true;
    this.swipeInitRetryCount = 0;
  }

  private cleanupSwipeListeners() {
    if (!this.swipeInitialized || !this.menuNavbar?.nativeElement) return;
    
    const menuElement = this.menuNavbar.nativeElement;
    menuElement.removeEventListener('touchstart', this.handleTouchStart);
    menuElement.removeEventListener('touchmove', this.handleTouchMove);
    menuElement.removeEventListener('touchend', this.handleTouchEnd);
    
    this.swipeInitialized = false;
  }

  // Bound event handlers to preserve 'this' context
  private handleTouchStart = (e: TouchEvent) => {
    if (e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartTime = Date.now();
    this.isSwiping = false;
  };

  private handleTouchMove = (e: TouchEvent) => {
    if (e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = Math.abs(touch.clientY - this.touchStartY);
    
    // Check if this is likely a horizontal swipe to the right
    if (deltaX > 10 && Math.abs(deltaX) > deltaY) {
      this.isSwiping = true;
      // Prevent vertical scrolling during horizontal swipe
      e.preventDefault();
    }
  };

  private handleTouchEnd = (e: TouchEvent) => {
    if (!this.isSwiping || e.changedTouches.length !== 1) return;
    
    const touch = e.changedTouches[0];
    const endTime = Date.now();
    
    // Calculate swipe metrics
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const time = endTime - this.touchStartTime;
    const velocity = distance / time;
    
    // Calculate angle (0° = horizontal right)
    const angle = Math.abs(Math.atan2(deltaY, Math.abs(deltaX)) * 180 / Math.PI);
    
    // Validate swipe to the right
    if (
      deltaX >= this.SWIPE_MIN_DISTANCE && // Minimum distance to the right
      time <= this.SWIPE_MAX_TIME && // Within time threshold
      velocity >= this.SWIPE_MIN_VELOCITY && // Minimum velocity
      angle <= this.SWIPE_ANGLE_THRESHOLD // Primarily horizontal
    ) {
      // Close the menu
      this.openMenu.set(false);
    }
    
    // Reset swipe state
    this.isSwiping = false;
  };

  switchLanguage(language: string) {
    this.languageService.languageState.set(language);
    this.translocoService.setActiveLang(language);
    this.currentLanguage = language;

    const urlTree = this.router.parseUrl(this.router.url);
    const localizedCommands = this.getLocalizedRouteCommands(language, urlTree);

    this.router.navigate(localizedCommands, {
      queryParams: urlTree.queryParams,
      fragment: urlTree.fragment || undefined,
      replaceUrl: true,
    });

    setTimeout(() => {
      this.openMenu.set(false);
    }, 10);
  }

  goToHome() {
    const lang = this.languageService.languageState();
    const urlTree = this.router.parseUrl(this.router.url);
    const primarySegments = this.getPrimarySegments(urlTree);
    const lastPrimarySegment = primarySegments[primarySegments.length - 1];
    const isCurrentRouteHome = lastPrimarySegment === 'home';

    if (lang !== '') {
      this.router.navigate(['/', lang, 'home'], {
        queryParams: isCurrentRouteHome ? urlTree.queryParams : undefined,
        fragment: isCurrentRouteHome ? urlTree.fragment || undefined : undefined,
      });
    }
  }

  goToAbout() {
    this.openMenu.set(false);
    const lang = this.languageService.languageState();
    if (lang !== '') {
      this.router.navigate([`${lang}/about`]);
    } else {
      this.router.navigate(['/about']);
    }
  }

  goToMarket() {
    this.openMenu.set(false);
    const lang = this.languageService.languageState();
    if (lang !== '') {
      this.router.navigate([`${lang}/market`]);
    } else {
      this.router.navigate(['/market']);
    }
  }

  goToDownloadApp() {
    const currentUrl = this.router.url;
    const isHomePage = currentUrl.includes('/home') || currentUrl === '/' || currentUrl === '/pt-br' || currentUrl === '/en';

    if (isHomePage) {
      // Se está na home, apenas emite o evento para scroll
      this.downloadAppClicked.emit();
    } else {
      // Se não está na home, redireciona para home e depois faz scroll
      const lang = this.languageService.languageState();
      const targetRoute = lang !== '' ? ['/', lang, 'home'] : ['/'];

      this.router.navigate(targetRoute).then(() => {
        // Aguarda a navegação completar e o componente renderizar
        setTimeout(() => {
          const appDownloadSection = document.querySelector('.app-download-section') as HTMLElement;

          if (!appDownloadSection) {
            console.warn('app-download-section not found');
            return;
          }

          // Get viewport dimensions
          const viewportHeight = window.innerHeight;

          // Get section position and dimensions
          const sectionRect = appDownloadSection.getBoundingClientRect();
          const sectionHeight = sectionRect.height;

          // Mathematical calculation to center the section in viewport
          const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const sectionTopFromDocument = sectionRect.top + currentScrollTop;

          // Calculate target scroll position to center the section
          const targetScrollTop = sectionTopFromDocument - (viewportHeight - sectionHeight) / 2;

          // Ensure we don't scroll past the document bounds
          const maxScrollTop = document.documentElement.scrollHeight - viewportHeight;
          const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));

          // Smooth scroll with enhanced easing for better UX
          window.scrollTo({
            top: finalScrollTop,
            behavior: 'smooth',
          });
        }, 100);
      });
    }
  }

  private getLocalizedRouteCommands(language: string, urlTree: UrlTree): string[] {
    const primarySegments = this.getPrimarySegments(urlTree);

    if (!primarySegments.length) {
      return ['/', language, 'home'];
    }

    const nextSegments = [...primarySegments];

    if (this.supportedLanguages.has(nextSegments[0])) {
      nextSegments[0] = language;
    } else {
      nextSegments.unshift(language);
    }

    return ['/', ...nextSegments];
  }

  private getPrimarySegments(urlTree: UrlTree): string[] {
    return (
      urlTree.root.children['primary']?.segments.map((segment) => segment.path) ??
      []
    );
  }
}
